import { Box, Center, Flex } from "@mantine/core";
import { Dropzone } from '@mantine/dropzone';
import { IconFileFilled, IconPhoto, IconUpload, IconX } from "@tabler/icons-react";
import { Button, Modal, Text } from "components";
import { IRootState } from "config/redux";
import { ServiceConstants } from "constant";
import { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ChatService } from "services";
import { ChatResponse } from "types";

const { REQUEST_CREDIT_ATTACHMENT_UPLOAD_ID, REQUEST_PENDING } = ServiceConstants;

interface ChatAttachmentUploadModalProps {
  chat: ChatResponse;
  opened: boolean;
  onClose: (val: boolean) => void;
  t: TFunction;
}

const ChatAttachmentUploadModal = ({ chat, opened, onClose, t }: ChatAttachmentUploadModalProps) => {
  const uploadServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_ATTACHMENT_UPLOAD_ID]);

  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);

  useEffect(() => {
    if (!opened) setSelectedFiles([]);
  }, [opened]);

  const createAttachmentType = useCallback(async () => {
    await ChatService.chatMessageAttachmentUpload(chat.id, chat.user.id, selectedFiles);
    onClose(false);
  }, [chat, selectedFiles]);
  const isLoading = useMemo(() => uploadServiceObject && uploadServiceObject.status === REQUEST_PENDING, [uploadServiceObject]);

  const removeFile = useCallback((file: any) => {
    setSelectedFiles(old => old.filter(el => el !== file))
  }, []);

  const toUpload = useMemo(() => selectedFiles.map((file, index) => (
    <Flex key={`selected-${index}`} align={'center'} mb={4}>
      <Box miw={24} mih={24}>
        <IconFileFilled color={'var(--mantine-color-primary-6)'} width={24} height={24} />
      </Box>
      <Text ml={6}>{file.name}</Text>
      <Button
        variant={'transparent'}
        size={'compact-xs'}
        px={0}
        onClick={() => removeFile(file)}
        miw={24}
        mih={24}
      >
        <IconX color={'var(--mantine-color-error)'} />
      </Button>
    </Flex>
  )), [selectedFiles]);

  return (
    <Modal opened={opened} onClose={() => onClose(false)} title={t('credit.documentation.delivered.new.title')}>
      <Dropzone
        onDrop={(files) => setSelectedFiles(old => ([...old, ...files]))}
        multiple={true}
        maxSize={30 * 1048576} // 30MB
        accept={['application/pdf, image/png, image/jpeg']}
      >
        <Flex justify={'center'} align={'center'} gap={'sm'} mih={220} style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept>
            <IconUpload size={52} color="var(--mantine-color-primary-6)" stroke={1.5} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={52} color="var(--mantine-color-red-6)" stroke={1.5} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconPhoto size={52} color="var(--mantine-color-dimmed)" stroke={1.5} />
          </Dropzone.Idle>
          <Box>
            <Text size={'xl'}>{t('credit.documentation.delivered.new.description.1')}</Text>
            <Text>{t('credit.documentation.delivered.new.description.2')}</Text>
          </Box>
        </Flex>
      </Dropzone>
      <Box mt={toUpload.length > 0 ? 'sm' : 0} >
        {toUpload}
      </Box>
      <Center mt={32}>
        <Button
          onClick={createAttachmentType}
          disabled={isLoading || selectedFiles.length === 0}
          loading={isLoading}
        >
          <Text>{t('action.upload')}</Text>
        </Button>
      </Center>
    </Modal>
  )
}

export default ChatAttachmentUploadModal;